import dayjs from 'dayjs';
import {
    AgencyAlertKind,
    clientDisplayName,
    IAgencyAlert,
    IClientBaseline,
    IPriorityClient,
    IHistoryProgress,
    IOutcomeMetric
} from '@innerhive-internal/innerhive-common';

/** A line of Bea's that Ask about this asks her about, with the report it is under. */
export interface IAskAboutQuestion {
    panelName: string;
    line: string;
}

/** One client worth watching, newest finding first, with why in a phrase. */
export interface IWorthWatchingRow {
    careMapId: string;
    clientName: string;
    /** Why they are worth watching, in their findings' own words, or a fixed line for a client with no finding in scope. */
    why: string;
    /** When the newest finding was observed, as YYYY-MM-DD; empty for a client with no finding in scope. */
    newestOn: string;
}

/** One client the worklist leaves out, with how they are doing in a line. */
export interface IRestOfCaseloadRow {
    careMapId: string;
    clientName: string;
    note: string;
}

/** A finding a why may name, and whether it counts towards the things raised past the named ones. */
interface IWhyFinding {
    title: string;
    /** Something that happened or a change against their baseline, as opposed to an ordinary observation. */
    isConcern: boolean;
}

/** How many findings a why names. */
const WHY_FINDINGS = 2;

/** What leads a why: something that happened, then a change against the baseline, then the rest. */
const KIND_RANK: { [key in AgencyAlertKind]: number } = { incident: 0, baseline: 1, observation: 2 };

const IS_CONCERN: { [key in AgencyAlertKind]: boolean } = { incident: true, baseline: true, observation: false };

const stripFullStop = (sentence: string): string => sentence.trim().replace(/\.$/, '');

const listOf = (items: string[]): string => {
    if (items.length <= 1) {
        return items.join('');
    }
    if (items.length === 2) {
        return `${items[0]} and ${items[1]}`;
    }
    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
};

/**
 * The sentences and worklists the Insights page writes itself, from the figures the digest already
 * holds: who is worth watching and why, and how the rest of the
 * caseload is doing.
 *
 * The same rule as every other sentence on the page: these describe what is on the record, name the
 * people it is about, and never say why anything happened.
 */
export class InsightsNarration {
    static inject = () => [];

    /** "Dorothy M.": enough to place a person in a sentence without printing their whole name. */
    shortName = (clientName: string): string => {
        const parts = clientName.trim().split(/\s+/).filter(Boolean);
        if (parts.length === 0) {
            return clientDisplayName(clientName);
        }
        if (parts.length === 1) {
            return parts[0];
        }
        return `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`;
    };

    /** Why a client is a priority, as a sentence: the incidents in the recent weeks, the newest of them, and whether more is raised than usual. */
    describePriorityWhy = (client: IPriorityClient): string => {
        const raised = client.raisedAboveUsual ? ' More has been raised about them than in their own earlier weeks.' : '';
        if (!client.latestIncident) {
            return 'More raised about them recently than in their own earlier weeks, with no incident recorded.';
        }
        const count = client.incidentCount === 1 ? 'An incident was recorded' : `${client.incidentCount} incidents were recorded`;
        const when = dayjs(client.latestIncident.observedOn).format('MMM D');
        return `${count} in the recent weeks, the latest on ${when}: ${stripFullStop(client.latestIncident.title)}.${raised}`;
    };

    /** What Ask about this sends Bea: her line and the report it is under. */
    askAbout = (params: IAskAboutQuestion): string => {
        const { panelName, line } = params;
        return `Tell me more about this, from ${panelName}: "${line}"`;
    };

    /** What the Needs Attention report's Ask Bea about this sends her: the full names of the clients it lists. */
    askAboutNeedsAttention = (clientNames: string[]): string =>
        `Tell me more about the clients in Needs Attention: ${listOf(clientNames.map(clientDisplayName))}.`;

    /** The outcome figures the caseload's history can support yet, and the ones still waiting on it. */
    splitOutcomes = (params: { outcomes: IOutcomeMetric[]; history: IHistoryProgress }): { shown: IOutcomeMetric[]; waiting: IOutcomeMetric[] } => {
        const { outcomes, history } = params;
        const shown = outcomes.filter(metric => history.hasEnough || !metric.definition.needsHistory);
        return { shown, waiting: outcomes.filter(metric => !shown.includes(metric)) };
    };

    /**
     * One row per client worth watching: the clients the map draws in that reading and the colour
     * key counts, so the three never disagree on who. A client something happened to is drawn red
     * and listed at the top of the Needs Attention report instead, so they are not here.
     * Newest finding first, each saying why in the findings' own words: something that happened
     * before a change against their baseline before anything else, so the why leads with the thing
     * a reader would go for. A client with three things raised is one row, not three, and a client
     * worth watching with no finding is still a row, since the map and the colour key already count
     * them.
     */
    worthWatching = (params: {
        baselines: IClientBaseline[];
        /** The findings, of every kind: a client can be worth watching on trends alone. */
        alerts: IAgencyAlert[];
    }): IWorthWatchingRow[] => {
        const { baselines, alerts } = params;
        const clients = baselines.filter(baseline => baseline.reading === 'worthWatching');
        const clientIds = new Set(clients.map(client => client.careMapId));
        const ordered = [...alerts]
            .filter(alert => clientIds.has(alert.careMapId))
            .sort((a, b) => (KIND_RANK[a.kind] - KIND_RANK[b.kind]) || b.observedOn.localeCompare(a.observedOn));

        const rows = new Map<string, { clientName: string; findings: IWhyFinding[]; newestOn: string }>();
        for (const alert of ordered) {
            const row = rows.get(alert.careMapId) ?? { clientName: alert.clientName, findings: [], newestOn: alert.observedOn };
            const title = stripFullStop(alert.title);
            if (!row.findings.some(finding => finding.title === title)) {
                row.findings.push({ title, isConcern: IS_CONCERN[alert.kind] });
            }
            row.newestOn = row.newestOn > alert.observedOn ? row.newestOn : alert.observedOn;
            rows.set(alert.careMapId, row);
        }

        const withFindings = [...rows.entries()]
            .map(([careMapId, row]) => ({
                careMapId,
                clientName: row.clientName,
                why: this.describeWhy(row.findings),
                newestOn: row.newestOn
            }))
            .sort((a, b) => b.newestOn.localeCompare(a.newestOn));

        const quiet = clients
            .filter(client => !rows.has(client.careMapId))
            .map(client => ({
                careMapId: client.careMapId,
                clientName: client.clientName,
                why: 'More raised about them recently than in their own earlier weeks',
                newestOn: ''
            }));
        return [...withFindings, ...quiet];
    };

    // Each finding stays a sentence of its own, in its own casing: run together they would need
    // lowercasing, which would turn a name or a title at the start of one into "dorothy's daughter".
    // Only concerns count towards "more things raised"; an ordinary observation is not one.
    private describeWhy = (findings: IWhyFinding[]): string => {
        const [first, second] = findings;
        if (!first) {
            return '';
        }
        if (!second) {
            return first.title;
        }
        const unnamed = findings.slice(WHY_FINDINGS).filter(finding => finding.isConcern).length;
        if (unnamed === 0) {
            return `${first.title}. ${second.title}.`;
        }
        return `${first.title}. ${second.title}. ${unnamed} more ${unnamed === 1 ? 'thing' : 'things'} raised.`;
    };

    /**
     * Everyone the Needs Attention report leaves out, with how they are doing: their
     * standing against their own baseline, and the newest thing on their record, which on a steady
     * caseload is usually the good news.
     */
    restOfCaseload = (params: { baselines: IClientBaseline[]; alerts: IAgencyAlert[]; watchingIds: Set<string> }): IRestOfCaseloadRow[] => {
        const { baselines, alerts, watchingIds } = params;
        const newestFirst = [...alerts].sort((a, b) => b.observedOn.localeCompare(a.observedOn));

        return baselines
            .filter(baseline => !watchingIds.has(baseline.careMapId))
            .map(baseline => ({
                careMapId: baseline.careMapId,
                clientName: baseline.clientName,
                note: this.describeRest({ baseline, latest: newestFirst.find(alert => alert.careMapId === baseline.careMapId) })
            }));
    };

    private describeRest = (params: { baseline: IClientBaseline; latest?: IAgencyAlert }): string => {
        const { baseline, latest } = params;
        const recent = latest ? ` Most recent on file: ${stripFullStop(latest.title)}.` : '';

        if (baseline.standing === 'unknown') {
            return baseline.volume.recordedRecently
                ? `Not enough history yet to compare against their own baseline.${recent}`
                : 'Nothing recent on record to compare against their own baseline.';
        }
        if (baseline.standing === 'settled') {
            return `Trending Better, with less raised about them recently than in their own earlier weeks and no incident recorded.${recent}`;
        }
        if (baseline.hasRecentIncident) {
            return `Something happened in the recent weeks.${recent}`;
        }
        if (baseline.standing === 'above') {
            return `More raised about them recently than in their own earlier weeks.${recent}`;
        }
        return `Steady against their own baseline.${recent}`;
    };
}
